/**
 * Media components for Hologram Club — trailers, reel grid, originals showcase.
 * Extracted from HoligramClub.jsx as part of the file-split refactor.
 *
 * Exports:
 *   - ytId()          YouTube-URL → video-ID extractor (shared with Modals)
 *   - Trailer         lazy-mounted <video> / YouTube iframe with audio leader
 *                     + global concurrency cap (never > 6 iframes at once)
 *   - TrailerReel     8-up grid hero reel
 *   - OriginalsShowcase  headline cards for is_original shows
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PlayCircle } from "lucide-react";
import { http, mediaUrl } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";

/* ==================== YouTube helpers + global iframe/audio state ==================== */
export function ytId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  return m ? m[1].split(/[?&#]/)[0] : null;
}

// Global concurrency cap — never more than this many iframes mounted at once.
const MAX_CONCURRENT_IFRAMES = 6;
const _activeIframes = new Set();
const _iframeListeners = new Set();
function _broadcastIframes() { _iframeListeners.forEach((fn) => fn(_activeIframes.size)); }

// Global "audio leader" — only one trailer plays audio at a time.
const AUDIO_EVENT = "hologram-audio-claim";

/* ==================== Trailer ==================== */
export function Trailer({ src, poster, className = "", testId, eager = false, allowAudio = false }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const idRef = useRef(Math.random().toString(36).slice(2));
  const [failed, setFailed] = useState(false);
  const [inView, setInView] = useState(eager);
  const [canMount, setCanMount] = useState(eager);
  const [audioOn, setAudioOn] = useState(false);
  const videoId = ytId(src);
  const resolved = mediaUrl(src);

  // Lazy-mount: only load once visible
  useEffect(() => {
    if (eager || !containerRef.current || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => setInView(e.isIntersecting || inView)),
      { rootMargin: "120px 0px", threshold: 0.05 }
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eager]);

  // Concurrency cap: only top-N visible iframes get to mount
  useEffect(() => {
    if (eager) { setCanMount(true); return; }
    if (!videoId) { setCanMount(inView); return; }
    if (!inView) {
      _activeIframes.delete(idRef.current);
      _broadcastIframes();
      setCanMount(false);
      return;
    }
    const onChange = () => {
      if (_activeIframes.has(idRef.current)) { setCanMount(true); return; }
      if (_activeIframes.size < MAX_CONCURRENT_IFRAMES) {
        _activeIframes.add(idRef.current);
        _broadcastIframes();
        setCanMount(true);
      } else {
        setCanMount(false);
      }
    };
    _iframeListeners.add(onChange);
    onChange();
    return () => {
      _iframeListeners.delete(onChange);
      if (_activeIframes.delete(idRef.current)) _broadcastIframes();
    };
  }, [inView, eager, videoId]);

  // Global audio leader: only one trailer can have audio on
  useEffect(() => {
    const handler = (e) => {
      if (e.detail !== idRef.current && audioOn) setAudioOn(false);
    };
    window.addEventListener(AUDIO_EVENT, handler);
    return () => window.removeEventListener(AUDIO_EVENT, handler);
  }, [audioOn]);

  useEffect(() => { setFailed(false); }, [resolved]);

  // Native video playback (when src is a local file, not YouTube)
  useEffect(() => {
    if (videoId || !inView) return;
    const el = videoRef.current;
    if (!el || !resolved || failed) return;
    el.muted = !audioOn;
    el.playsInline = true;
    const tryPlay = () => el.play().catch(() => {});
    tryPlay();
    el.addEventListener("loadeddata", tryPlay);
    el.addEventListener("canplay", tryPlay);
    return () => {
      el.removeEventListener("loadeddata", tryPlay);
      el.removeEventListener("canplay", tryPlay);
    };
  }, [resolved, failed, videoId, inView, audioOn]);

  const claimAudio = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    const next = !audioOn;
    setAudioOn(next);
    if (next) window.dispatchEvent(new CustomEvent(AUDIO_EVENT, { detail: idRef.current }));
  };

  const Poster = (
    <>
      <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover ken-burns hologram-isolated" />
      <div className="absolute inset-0 scanlines opacity-25 pointer-events-none" />
    </>
  );

  const AudioBtn = allowAudio ? (
    <button
      onClick={claimAudio}
      data-testid={testId ? `${testId}-audio-toggle` : undefined}
      className="absolute bottom-2 right-2 z-20 w-9 h-9 rounded-full border border-[#00E5FF]/60 bg-black/70 backdrop-blur flex items-center justify-center text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black transition-all"
      aria-label={audioOn ? "Mute" : "Unmute"}
    >
      {audioOn ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.5 8.5a4 4 0 010 7"/><path d="M19 5a8 8 0 010 14"/></svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>
      )}
    </button>
  ) : null;

  if (failed || (!resolved && !videoId)) {
    return (
      <div ref={containerRef} className={`${className} relative overflow-hidden`}>
        {Poster}
      </div>
    );
  }

  if (videoId) {
    if (!canMount) {
      return (
        <div ref={containerRef} className={`${className} relative overflow-hidden`}>
          {Poster}
          {AudioBtn}
        </div>
      );
    }
    const params = new URLSearchParams({
      autoplay: "1",
      mute: audioOn ? "0" : "1",
      loop: "1",
      playlist: videoId,
      controls: "0", modestbranding: "1", rel: "0",
      iv_load_policy: "3", playsinline: "1", disablekb: "1", fs: "0",
    });
    return (
      <div ref={containerRef} className={`${className} relative overflow-hidden bg-black`}>
        {Poster}
        <iframe
          key={`${videoId}-${audioOn ? "on" : "off"}`}
          src={`https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`}
          title="trailer"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none hologram-isolated"
          style={{ width: "200%", height: "200%" }}
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          loading="lazy"
          onError={() => setFailed(true)}
          data-testid={testId}
        />
        <div className="absolute inset-0 scanlines opacity-25 pointer-events-none" />
        {AudioBtn}
      </div>
    );
  }

  if (!inView) {
    return <div ref={containerRef} className={`${className} relative overflow-hidden`}>{Poster}</div>;
  }
  return (
    <div ref={containerRef} className={`${className} relative overflow-hidden`}>
      <video
        ref={videoRef}
        src={resolved}
        poster={poster}
        autoPlay loop muted={!audioOn} playsInline preload="metadata"
        crossOrigin="use-credentials"
        onError={() => setFailed(true)}
        className="absolute inset-0 w-full h-full object-cover hologram-isolated"
        data-testid={testId}
      />
      {AudioBtn}
    </div>
  );
}

/* ==================== Trailer Reel ==================== */
export function TrailerReel({ shows, onWatch }) {
  if (!shows?.length) return null;
  return (
    <section
      id="reel"
      data-testid="trailer-reel"
      className="relative py-16 md:py-24 border-b border-white/5 bg-[#050505] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-10">
        <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#FF007F] flex items-center gap-3">
          <span className="ring-dot" />
          <span>Live Reel · All Holograms · Now Playing</span>
        </div>
        <h2 className="mt-4 font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">
          Every artist, <br /><span className="holofoil">every second.</span>
        </h2>
        <p className="mt-4 text-white/60 max-w-xl">
          Sixteen photonic reconstructions running simultaneously across two
          venues. Hover to spotlight. Click to enter the room.
        </p>
      </div>

      <div className="px-3 md:px-6">
        {/* MOBILE: horizontal swipe marquee — single row, snap-to-card, scrolls
            sideways like a Vegas Strip pillar reel. Hidden ≥ md. */}
        <div
          className="md:hidden -mx-3 px-3 flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2"
          style={{
            scrollSnapType: "x mandatory",
            scrollPaddingLeft: "12px",
            WebkitOverflowScrolling: "touch",
          }}
          data-testid="trailer-reel-scroller"
        >
          {shows.map((s) => {
            const cardImage = s.character_image_url ? mediaUrl(s.character_image_url) : s.image;
            return (
              <button
                key={`m-${s.id}`}
                onClick={() => onWatch(s)}
                data-testid={`reel-m-${s.id}`}
                className="group relative shrink-0 text-left snap-start focus:outline-none"
                style={{ width: "44vw" }}
              >
                <div className="relative aspect-[2/3] overflow-hidden border border-white/10 group-hover:border-[#00E5FF] group-active:border-[#FF007F] transition-all duration-500">
                  <img
                    src={cardImage}
                    alt={s.artist}
                    className="absolute inset-0 w-full h-full object-cover ken-burns hologram-isolated hologram-projection"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 scanlines opacity-25 pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                  {s.status === "live" && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 border border-[#FF007F]/70 bg-black/70 backdrop-blur px-1.5 py-0.5 font-mono-ui text-[8px] tracking-[0.28em] uppercase text-[#FF007F]">
                      <span className="ring-dot" style={{ width: 4, height: 4 }} /> Live
                    </div>
                  )}
                  <div className="absolute top-2 right-2 inline-flex items-center gap-1 border border-white/15 bg-black/60 backdrop-blur px-1.5 py-0.5 font-mono-ui text-[8px] tracking-[0.22em] uppercase text-white/80">
                    {s.venue === "la" ? "LA" : "VEG"}
                  </div>
                  {s.is_original && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 border border-[#FFD166]/70 bg-black/70 backdrop-blur px-1.5 py-0.5 font-mono-ui text-[8px] tracking-[0.28em] uppercase text-[#FFD166]" style={{ marginTop: s.status === "live" ? "20px" : "0" }}>
                      Original
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[#00E5FF] bg-black/55 backdrop-blur px-1.5 py-0.5">
                    {s.slot}
                  </div>
                </div>
                <div className="mt-3 font-display font-black tracking-tighter text-base leading-tight text-white group-hover:text-[#00E5FF] transition-colors">
                  {s.artist}
                </div>
              </button>
            );
          })}
        </div>

        {/* iPhone swipe hint — only renders on mobile and once-per-session */}
        <div className="md:hidden mt-2 mb-4 font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/35 text-center" data-testid="trailer-reel-swipe-hint">
          ← Swipe to see all 20 holograms →
        </div>

        {/* DESKTOP / TABLET: original grid — hidden on mobile */}
        <div className="hidden md:grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-3">
          {shows.map((s) => {
            const cardImage = s.character_image_url ? mediaUrl(s.character_image_url) : s.image;
            return (
              <button
                key={s.id}
                onClick={() => onWatch(s)}
                data-testid={`reel-${s.id}`}
                className="group relative text-left focus:outline-none"
              >
                <div className="relative aspect-[2/3] overflow-hidden border border-white/10 group-hover:border-[#00E5FF] transition-all duration-500">
                  <img
                    src={cardImage}
                    alt={s.artist}
                    className="absolute inset-0 w-full h-full object-cover ken-burns hologram-isolated hologram-projection opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                    data-testid={`reel-img-${s.id}`}
                  />
                  <div className="absolute inset-0 scanlines opacity-25 pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                  {s.status === "live" && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 border border-[#FF007F]/70 bg-black/70 backdrop-blur px-1.5 py-0.5 font-mono-ui text-[8px] tracking-[0.28em] uppercase text-[#FF007F]">
                      <span className="ring-dot" style={{ width: 4, height: 4 }} /> Live
                    </div>
                  )}
                  <div className="absolute top-2 right-2 inline-flex items-center gap-1 border border-white/15 bg-black/60 backdrop-blur px-1.5 py-0.5 font-mono-ui text-[8px] tracking-[0.22em] uppercase text-white/80">
                    {s.venue === "la" ? "LA" : "VEG"}
                  </div>
                  {s.is_original && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 border border-[#FFD166]/70 bg-black/70 backdrop-blur px-1.5 py-0.5 font-mono-ui text-[8px] tracking-[0.28em] uppercase text-[#FFD166]" data-testid={`reel-original-${s.id}`} style={{ marginTop: s.status === "live" ? "20px" : "0" }}>
                      Original
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 font-mono-ui text-[9px] md:text-[8px] tracking-[0.22em] uppercase text-[#00E5FF] bg-black/55 backdrop-blur px-1.5 py-0.5">
                    {s.slot}
                  </div>
                </div>
                <div className="mt-3 font-display font-black tracking-tighter text-base lg:text-sm leading-tight text-white group-hover:text-[#00E5FF] transition-colors">
                  {s.artist}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ==================== Originals Showcase ==================== */
export function OriginalsShowcase({ shows, onWatch }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [buyingId, setBuyingId] = useState(null);

  const originals = shows.filter((s) => s.is_original);
  if (!originals.length) return null;

  const buyTrack = async (e, show) => {
    e.stopPropagation();
    e.preventDefault();
    if (isOwner) { onWatch(show); return; }
    if (!user) {
      toast.message("Sign in to buy the track.");
      navigate("/hologram-club/login");
      return;
    }
    setBuyingId(show.id);
    try {
      const { data } = await http.post("/payments/ppv/checkout", {
        show_id: show.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBuyingId(null);
    }
  };

  return (
    <section
      id="originals"
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#050007]"
      data-testid="originals-showcase"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD166] to-transparent" />
      <div className="max-w-[1600px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12 md:mb-16">
          <div>
            <div className="font-mono-ui text-[11px] tracking-[0.32em] uppercase text-[#FFD166] inline-flex items-center gap-3">
              <span className="ring-dot" style={{ background: "#FFD166", boxShadow: "0 0 12px #FFD166" }} />
              Hologram Club Originals
            </div>
            <h2 className="mt-4 font-display font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
              Born here.<br />
              <span className="text-transparent" style={{ WebkitTextStroke: "1.5px #FFD166" }}>Heard nowhere else.</span>
            </h2>
            <p className="mt-6 max-w-xl font-body text-base text-white/65 leading-relaxed">
              Original characters, original tracks. These holograms were dreamed up,
              composed, and produced inside the club. Every dollar goes to the artists
              you'd never meet otherwise.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/hologram-club/demo"
              data-testid="originals-launch-demo"
              className="group inline-flex items-center gap-3 bg-[#FFD166] text-black hover:bg-white transition-colors font-mono-ui text-[11px] tracking-[0.32em] uppercase font-bold py-3.5 px-5"
            >
              <span className="ring-dot" style={{ background: "#FF007F", boxShadow: "0 0 10px #FF007F" }} />
              Launch Investor Demo
              <PlayCircle size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40">
              {originals.length} {originals.length === 1 ? "act" : "acts"} · 100% original IP
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
          {originals.map((s, i) => {
            const ppv = Number(s.ppv_price_usd || 0);
            const charImg = s.character_image_url ? mediaUrl(s.character_image_url) : s.image;
            const buying = buyingId === s.id;
            return (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group relative border border-[#FFD166]/30 bg-[#0B0608] overflow-hidden hover:border-[#FFD166]/80 transition-colors duration-500"
                data-testid={`original-card-${s.id}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={charImg}
                    alt={s.artist}
                    className="absolute inset-0 w-full h-full object-cover ken-burns hologram-isolated hologram-projection opacity-95 group-hover:opacity-100 transition-opacity duration-700"
                  />
                  {s.trailer_url && (
                    <Trailer
                      src={s.trailer_url}
                      poster={charImg}
                      className="absolute inset-0 w-full h-full object-cover mix-blend-screen opacity-30 group-hover:opacity-55 transition-opacity duration-700"
                      testId={`original-trailer-${s.id}`}
                    />
                  )}
                  <div className="absolute inset-0 scanlines opacity-25 pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />

                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 border border-[#FFD166] bg-[#FFD166] text-black px-2.5 py-1 font-mono-ui text-[10px] tracking-[0.32em] uppercase font-bold">
                    Original
                  </div>
                  {s.genre && (
                    <div className="absolute top-4 right-4 inline-flex items-center gap-1 border border-white/15 bg-black/70 backdrop-blur px-2.5 py-1 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/80">
                      {s.genre}
                    </div>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                    <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">
                      {s.slot} · {s.venue === "la" ? "Los Angeles" : "Las Vegas"}
                    </div>
                    <div className="mt-2 font-display font-black tracking-tighter text-3xl md:text-4xl leading-[0.95]">
                      {s.artist}
                    </div>
                    {s.character_description && (
                      <p className="mt-3 font-body text-sm text-white/65 line-clamp-2 leading-relaxed">
                        {s.character_description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="p-5 md:p-6 flex flex-wrap items-center gap-3 border-t border-[#FFD166]/15">
                  <button
                    type="button"
                    onClick={(e) => buyTrack(e, s)}
                    disabled={buying || (!isOwner && ppv <= 0)}
                    data-testid={`buy-original-${s.id}`}
                    className="inline-flex items-center gap-2 bg-[#FFD166] hover:bg-white text-black font-mono-ui text-[11px] tracking-[0.28em] uppercase font-bold py-3 px-5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {buying ? "Redirecting..." :
                      isOwner ? `Listen · Owner Access` :
                      ppv > 0 ? `Buy The Original Track · $${ppv.toFixed(2)}` : "Free To Stream"}
                  </button>
                  <button
                    type="button"
                    onClick={() => onWatch(s)}
                    data-testid={`preview-original-${s.id}`}
                    className="inline-flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/70 hover:text-[#FFD166] transition-colors"
                  >
                    <PlayCircle size={13} /> Preview
                  </button>
                  {s.audio_url ? (
                    <span className="ml-auto inline-flex items-center gap-1.5 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[#00E5FF]" data-testid={`mp3-ready-${s.id}`} title="Original MP3 track ready">
                      <span className="ring-dot" style={{ width: 5, height: 5, background: "#00E5FF", boxShadow: "0 0 8px #00E5FF" }} />
                      MP3 Ready
                    </span>
                  ) : (
                    <span className="ml-auto inline-flex items-center gap-1.5 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-white/30" title="Audio coming soon">
                      Audio Pending
                    </span>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
